import { motion } from "framer-motion";
import { Globe2, ArrowDownUp } from "lucide-react";
import { Reveal, SectionTag, KineticLine } from "@/components/Reveal";
import Ticker from "@/components/Ticker";

const founders = [
  {
    name: "Utsav Sheth",
    role: "Founder & CEO",
    img: "/assets/founder-utsav.png",
    bio: "The visionary behind the Ollexo Group of Companies, redefining the metal recycling sector through innovation, sustainability and global growth. Under his leadership, Fluton Global Metalcraft FZCO extends the group's legacy from Dubai — transforming verified scrap into a strategic resource for industries worldwide, with quality and transparent dealing on every consignment.",
  },
];

const values = [
  ["Integrity", "Transparent pricing and honest grading on every lot we trade."],
  ["Reliability", "On-time dispatch against contract — our word carries weight."],
  ["Sustainability", "Circular trade that keeps metal in use and out of landfill."],
  ["Partnership", "Long-term relationships with yards, mills and smelters worldwide."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* PAGE HERO */}
      <section className="relative pt-40 pb-24 bg-ink noise overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src="/assets/warehouse.jpg" alt="Industrial warehouse" className="w-full h-full object-cover opacity-25 grayscale-[40%]" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="font-tech text-xs uppercase tracking-[0.3em] text-copper mb-6"
          >
            About Fluton
          </motion.p>
          <h1 className="font-display font-black uppercase text-white tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="about-headline">
            <KineticLine text="Two companies." delay={0.15} />
            <KineticLine text="One corridor." delay={0.3} className="text-copper" />
          </h1>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 md:py-32" data-testid="about-story-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="01" label="Our Story" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <Reveal>
              <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02]">
                Born in India. <span className="text-copper">Scaled in Dubai.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-gray-600 leading-relaxed">
                Fluton Global Metalcraft FZCO is proud to be a group company of the Ollexo Group
                of Companies. Ollexo built its name in India — sourcing, inspecting and importing
                premium recyclable metals with an uncompromising eye on quality. From Dubai,
                Fluton carries that legacy forward: exporting and trading those same verified
                materials to the world.
              </p>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Together, the group forms a single trade corridor — Indian sourcing depth on one
                end, Dubai's logistics and free-zone advantages on the other. Our clients get one
                accountable partner across the entire route.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="about-corridor-cards">
            <Reveal>
              <div className="border border-gray-200 bg-white p-10 h-full">
                <Globe2 size={28} className="text-copper" />
                <p className="font-tech text-xs uppercase tracking-[0.25em] text-gray-500 mt-6">India · Import</p>
                <h3 className="font-display font-bold text-2xl text-navy tracking-tight mt-2">Ollexo Group of Companies</h3>
                <p className="mt-4 text-sm text-gray-600 leading-relaxed">
                  Sourcing, quality assurance and import operations from the Indian subcontinent —
                  the supply engine of the group.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-navy bg-navy p-10 h-full noise">
                <ArrowDownUp size={28} className="text-copper relative z-10" />
                <p className="font-tech text-xs uppercase tracking-[0.25em] text-gray-400 mt-6 relative z-10">Dubai · Export & Trade</p>
                <h3 className="font-display font-bold text-2xl text-white tracking-tight mt-2 relative z-10">
                  Fluton Global Metalcraft FZCO
                </h3>
                <p className="mt-4 text-sm text-gray-300 leading-relaxed relative z-10">
                  Export, trading and logistics from Dubai Silicon Oasis — connecting verified
                  supply to mills, smelters and manufacturers worldwide.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Ticker />

      {/* MISSION / VISION */}
      <section className="py-24 md:py-32 bg-white border-b border-gray-200" data-testid="about-mission-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="02" label="Mission & Vision" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="relative h-full min-h-[380px] overflow-hidden group bg-ink">
                <img src="/assets/mission.jpg" alt="Mission" className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-10">
                  <p className="font-tech text-xs uppercase tracking-[0.25em] text-copper">Mission</p>
                  <p className="mt-4 text-gray-100 leading-relaxed max-w-md" data-testid="about-mission-text">
                    Deliver premium-grade recyclable metals that meet the highest international
                    standards — with ethical sourcing, seamless logistics and transparent dealing
                    on every single consignment.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative h-full min-h-[380px] overflow-hidden group bg-ink">
                <img src="/assets/crane-scrap.jpg" alt="Vision" className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-10">
                  <p className="font-tech text-xs uppercase tracking-[0.25em] text-copper">Vision</p>
                  <p className="mt-4 text-gray-100 leading-relaxed max-w-md" data-testid="about-vision-text">
                    A world where industrial growth and environmental responsibility move forward
                    together — where every piece of scrap metal becomes a building block of a
                    cleaner, more resilient future.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-24 md:py-32" data-testid="about-leadership-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="03" label="Leadership" />
          <Reveal>
            <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02] mb-16">
              The man behind the metal.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 max-w-2xl gap-6">
            {founders.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.1}>
                <div className="group border border-gray-200 bg-white hover:-translate-y-1.5 transition-transform duration-300" data-testid={`about-founder-${i}`}>
                  <div className="h-80 overflow-hidden bg-paper">
                    <img
                      src={f.img}
                      alt={f.name}
                      className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8 border-t border-gray-200">
                    <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-copper">{f.role}</p>
                    <h3 className="font-display font-bold text-2xl text-navy tracking-tight mt-2">{f.name}</h3>
                    <p className="mt-4 text-sm text-gray-600 leading-relaxed">{f.bio}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-ink noise py-24 md:py-32" data-testid="about-values-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <SectionTag no="04" label="What We Stand For" light />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10 mt-8">
            {values.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="h-full border-b border-r border-white/10 p-8" data-testid={`about-value-${i}`}>
                  <p className="font-tech text-xs tracking-[0.25em] text-copper">0{i + 1}</p>
                  <h3 className="font-display font-bold text-xl text-white tracking-tight mt-3">{title}</h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
